/**
 * Category-aware size profiles for ZAMMER.
 *
 * Single source of truth for which sizes a seller can pick for a given category,
 * plus one canonical comparator so sizes sort correctly everywhere (seller forms
 * + buyer product pages). Replaces the duplicated `SIZE_RANK` maps that previously
 * lived in UploadCatalogue / ProductDetailPage / ProductVariantSelector.
 *
 * Rule (from product owner): sizes are decided per garment group. Because the
 * Level-2 "Western Wear" mixes tops AND bottoms, rules are keyed at Level 3
 * (garment) with Level-2 / Level-1 defaults that siblings inherit.
 *
 * The backend Product variant `size` enum (backend/models/Product.js) must
 * include every value below or saves are rejected — keep the two in sync.
 */

// --- Size sets ---------------------------------------------------------------

// Alphabetical. Kept as '2XL' (not 'XXL') to match existing data + backend enum.
export const ALPHA_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

// Numeric waist sizes for bottom wear.
export const WAIST_SIZES = ['28', '30', '32', '34', '36', '38', '40', '42', '44'];

// Sarees: free size + the extra options requested by the product owner.
export const SAREE_SIZES = ['Free Size', '6-9M', '9-12M', '12-13Y', '13-14Y'];

// Lehengas: the normal alphabetical ladder plus the two un-sized options that
// only lehengas are sold in. Semi-stitched and unstitched pieces often ship
// without a fixed size at all, so a seller needs to say so rather than picking
// an arbitrary M.
export const LEHENGA_SIZES = [...ALPHA_SIZES, 'Free Size', 'One Size'];

// Kids age sizes. Boys carry the full range; girls stop one step earlier.
export const KIDS_BOYS_AGE_SIZES = [
  '0-3M', '3-6M', '6-9M', '9-12M', '12-18M', '18-24M',
  '2-3Y', '3-4Y', '4-5Y', '5-6Y', '6-7Y', '7-8Y', '8-9Y', '9-10Y',
  '10-11Y', '11-12Y', '12-13Y', '13-14Y', '14-15Y', '15-16Y', '16-17Y'
];
export const KIDS_GIRLS_AGE_SIZES = KIDS_BOYS_AGE_SIZES.slice(0, -1); // up to 15-16Y

// --- Category → sizes resolution --------------------------------------------

// 4-level hierarchy overrides, keyed by Level 3 (garment).
const L3_WAIST = new Set([
  'Jeans', 'Trousers', 'Shorts',                  // Men > Western Wear
  'Track Pants', 'Sports Shorts',                 // Men > Sportswear
  'Jeans & Jeggings', 'Pants & Palazzos', 'Skirts' // Women > Western Wear
]);
const L3_SAREE = new Set([
  'Sarees, Blouses & Petticoats'                  // Women > Ethnic Wear
]);
const L3_LEHENGA = new Set([
  'Lehengas'                                      // Women > Ethnic Wear
]);

function fromHierarchy(level1, level2, level3) {
  if (level1 === 'Kids Fashion') {
    return level2 === 'Girls Wear' ? KIDS_GIRLS_AGE_SIZES : KIDS_BOYS_AGE_SIZES;
  }
  if (level3 && L3_SAREE.has(level3)) return SAREE_SIZES;
  // Kids is handled above, so Kids > Girls Wear > Lehenga Choli keeps the age
  // ladder — children's clothing is sized by age, not Free/One Size.
  if (level3 && L3_LEHENGA.has(level3)) return LEHENGA_SIZES;
  if (level3 && L3_WAIST.has(level3)) return WAIST_SIZES;
  return ALPHA_SIZES; // Men/Women default; siblings inherit this
}

// Legacy 2-level system (AddProduct / EditProduct), keyed by subCategory garment.
const OLD_WAIST = new Set(['Jeans', 'Skirts']); // only garments the owner named numeric
const OLD_SAREE = new Set(['Sarees']);
// Kept separate from L3_LEHENGA even though both are currently 'Lehengas': the
// two systems have drifted before (OLD_WAIST is deliberately narrower than
// L3_WAIST), and Add/Edit Product resolve through here, not the hierarchy. A
// lehenga edited on those pages needs Free Size / One Size just as much as one
// created through the catalogue flow.
const OLD_LEHENGA = new Set(['Lehengas']);

function fromOld(category, subCategory) {
  if (category === 'Kids') return KIDS_BOYS_AGE_SIZES;
  if (subCategory && OLD_SAREE.has(subCategory)) return SAREE_SIZES;
  if (subCategory && OLD_LEHENGA.has(subCategory)) return LEHENGA_SIZES;
  if (subCategory && OLD_WAIST.has(subCategory)) return WAIST_SIZES;
  return ALPHA_SIZES;
}

/**
 * Resolve the allowed sizes for a category. Prefers the 4-level hierarchy
 * (catalogue flow); falls back to the legacy category/subCategory (Add/Edit).
 * Always returns a non-empty array.
 */
export function getSizesForCategory({ level1, level2, level3, category, subCategory } = {}) {
  if (level1) return fromHierarchy(level1, level2, level3);
  if (category) return fromOld(category, subCategory);
  return ALPHA_SIZES;
}

// --- Canonical ordering ------------------------------------------------------

// Sort key: [typeBucket, valueWithinBucket]. Buckets keep mixed/legacy data
// deterministic; a real product only ever carries one profile.
function sizeKey(size) {
  const s = String(size).trim();

  const alphaIdx = ALPHA_SIZES.indexOf(s);
  if (alphaIdx !== -1) return [0, alphaIdx];

  // Un-sized options share a bucket so they sit together after the alphabetical
  // ladder. Without an entry here "One Size" would fall through to the unknown
  // bucket below and sort after the kids age ranges.
  if (/^free\s*size$/i.test(s)) return [1, 0];
  if (/^one\s*size$/i.test(s)) return [1, 1];

  if (/^\d+$/.test(s)) return [2, parseInt(s, 10)]; // numeric waist

  // Age range, e.g. "0-3M", "2-3Y", "6-9M" — order by lower bound in months.
  const m = s.match(/^(\d+)\s*-\s*\d+\s*([MY])$/i);
  if (m) {
    const n = parseInt(m[1], 10);
    return [3, m[2].toUpperCase() === 'Y' ? n * 12 : n];
  }

  return [9, 0]; // unknown/legacy → pushed last
}

/** Numeric rank for a single size (handy for one-off comparisons). */
export function sizeRank(size) {
  const [bucket, value] = sizeKey(size);
  return bucket * 100000 + value;
}

/** Comparator: alphabetical (XS→5XL), then Free Size, then numeric, then age. */
export function compareSizes(a, b) {
  const ka = sizeKey(a);
  const kb = sizeKey(b);
  return ka[0] - kb[0] || ka[1] - kb[1];
}
